import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, X, Code, MessageCircle, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { PRODUCT_DEV_TIERS, PRODUCT_DEV_SYMBOL } from '@/lib/productDevPricing';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

function fmt(n) { return Number(n).toLocaleString('en-US'); }

/**
 * Paywall modal opened when a client without active Product / Software Development
 * plan clicks "Submit Project Brief". Requesting a Starter/Growth plan emails the
 * admin (no Stripe link); the Custom tier opens the chat with the Account Manager.
 */
export default function ProductDevPaywallModal({ open, firmId, onClose, onSavePendingBrief, onOpenChat }) {
  const [loading, setLoading] = useState(null);
  const [requestedTier, setRequestedTier] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    setRequestedTier(null);
    setError('');
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  const handleChoose = async (tier) => {
    setError('');
    if (tier.isCustom) {
      // Custom: persist the brief (admin will see it once active) and open chat.
      if (onSavePendingBrief) onSavePendingBrief(tier.id);
      if (onOpenChat) onOpenChat();
      onClose();
      return;
    }
    setLoading(tier.id);
    try {
      if (onSavePendingBrief) onSavePendingBrief(tier.id);
      await axios.post(`${API}/web-dev/${firmId}/request-plan`, { tier: tier.id }, { headers: authHeaders() });
      setRequestedTier(tier);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not send your request. Please try again.');
    } finally {
      setLoading(null);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center px-4 py-8 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="product-dev-paywall-title"
      data-testid="product-dev-paywall-modal"
    >
      <button
        type="button"
        aria-label="Close paywall"
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full max-w-4xl rounded-3xl bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 border border-violet-500/30 p-7 shadow-2xl shadow-violet-900/40 my-auto">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 text-zinc-500 hover:text-white transition-colors"
          aria-label="Close"
          data-testid="product-dev-paywall-close"
        >
          <X className="w-5 h-5" />
        </button>

        {requestedTier ? (
          <div className="py-8 text-center" data-testid="product-dev-request-success">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-500/30 mx-auto mb-5">
              <CheckCircle2 className="w-7 h-7 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit' }}>
              Request sent
            </h2>
            <p className="text-zinc-400 text-sm max-w-md mx-auto">
              Thanks for requesting the <span className="text-white font-medium">{requestedTier.label}</span> plan.
              Our team has been notified and will reach out shortly to arrange access and payment. Your project brief stays saved.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="mt-6 px-6 py-2.5 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 text-white text-sm font-semibold hover:shadow-lg hover:shadow-violet-500/30"
              data-testid="product-dev-request-success-close"
            >
              Done
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-violet-500/30">
                <Code className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 id="product-dev-paywall-title" className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>
                  Choose your Product Development plan
                </h2>
                <p className="text-zinc-400 text-sm mt-1">
                  Your project brief stays saved. Request a plan and our team will reach out to get you set up.
                </p>
              </div>
            </div>

            {error && (
              <div className="mb-5 p-3 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-300 text-sm" data-testid="product-dev-request-error">
                {error}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {PRODUCT_DEV_TIERS.map((tier) => (
                <div
                  key={tier.id}
                  className={`relative rounded-2xl border p-5 flex flex-col ${
                    tier.highlight
                      ? 'border-violet-500/60 bg-gradient-to-b from-violet-600/10 to-transparent'
                      : 'border-white/10 bg-white/[0.02]'
                  }`}
                  data-testid={`product-dev-tier-${tier.id}`}
                >
                  {tier.highlight && (
                    <div className="absolute -top-2.5 right-4 px-2.5 py-0.5 rounded-full bg-violet-500 text-white text-[10px] font-semibold tracking-wider">
                      POPULAR
                    </div>
                  )}
                  <h3 className="text-lg font-semibold text-white">{tier.label}</h3>
                  <div className="mt-2 mb-3">
                    {tier.priceUSD ? (
                      <div className="flex items-baseline gap-1">
                        <span className="text-3xl font-bold text-white">{PRODUCT_DEV_SYMBOL}{fmt(tier.priceUSD)}</span>
                        <span className="text-zinc-500 text-sm">USD</span>
                      </div>
                    ) : (
                      <div className="text-sm text-violet-300 font-medium">{tier.customPriceLabel}</div>
                    )}
                  </div>
                  <p className="text-zinc-400 text-sm mb-3">{tier.description}</p>
                  <ul className="space-y-2 mb-5 flex-1">
                    {tier.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2 text-sm text-zinc-300">
                        <Check className="w-4 h-4 mt-0.5 text-violet-400 flex-shrink-0" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    onClick={() => handleChoose(tier)}
                    disabled={loading === tier.id}
                    className={`w-full py-2.5 px-4 rounded-xl text-sm font-semibold transition-all flex items-center justify-center gap-2 ${
                      tier.isCustom
                        ? 'bg-white/[0.06] hover:bg-white/[0.1] text-white border border-white/10'
                        : 'bg-gradient-to-r from-violet-600 to-indigo-600 hover:shadow-lg hover:shadow-violet-500/30 text-white'
                    } disabled:opacity-60`}
                    data-testid={`product-dev-choose-${tier.id}`}
                  >
                    {tier.isCustom && <MessageCircle className="w-4 h-4" />}
                    {loading === tier.id ? 'Sending…' : tier.cta}
                  </button>
                </div>
              ))}
            </div>

            <p className="mt-5 text-xs text-zinc-500 text-center">
              One-off engagement. Request a plan and our team confirms access and payment with you directly. For custom scope, our Account Manager will reach out in the chat.
            </p>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
